import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Mail, Menu, X, Clock, MapPin } from 'lucide-react';
import { companyInfo } from '../data/mock';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigation = [
    { name: 'Home', path: '/' },
    { name: 'CSCS Cards', path: '/cscs-services' },
    { name: 'Apply CSCS', path: '/apply-cscs', highlight: true },
    { name: 'Book Test', path: '/book-test', highlight: true },
    { name: 'NVQ Courses', path: '/nvq-courses' },
    { name: 'CPCS', path: '/cpcs-courses' },
    { name: 'About Us', path: '/about' },
    { name: 'Contact', path: '/contact' }
  ];

  return (
    <>
      {/* Top Info Bar - Enhanced */}
      <div className="bg-gradient-to-r from-construction-darkblue via-blue-900 to-construction-darkblue text-white py-2.5 hidden md:block relative overflow-hidden">
        {/* Animated background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-full bg-construction-yellow blur-3xl animate-pulse"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="flex justify-between items-center text-sm">
            <div className="flex items-center gap-6">
              <a 
                href={`mailto:${companyInfo.email}`} 
                className="flex items-center gap-2 hover:text-construction-yellow transition-all duration-300 hover:scale-105"
              >
                <div className="bg-white/10 p-1.5 rounded-lg backdrop-blur-sm">
                  <Mail size={14} />
                </div>
                <span className="font-medium">{companyInfo.email}</span>
              </a>
              <div className="flex items-center gap-2 text-gray-200">
                <div className="bg-white/10 p-1.5 rounded-lg backdrop-blur-sm">
                  <Clock size={14} />
                </div>
                <span>Mon-Fri: 8AM-6PM | Sat: 9AM-2PM</span>
              </div>
              <div className="flex items-center gap-2 text-gray-200">
                <div className="bg-white/10 p-1.5 rounded-lg backdrop-blur-sm">
                  <MapPin size={14} />
                </div>
                <span>London, UK</span>
              </div>
            </div>
            <a 
              href={`tel:${companyInfo.phone}`} 
              className="flex items-center gap-2 font-bold bg-construction-yellow text-construction-darkblue px-4 py-1.5 rounded-full hover:bg-yellow-400 transition-all duration-300 hover:scale-105 shadow-lg"
            >
              <Phone size={14} />
              <span>{companyInfo.phone}</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main Header - Enhanced */}
      <header 
        className={`sticky top-0 z-50 transition-all duration-500 ${
          isScrolled 
            ? 'bg-white/95 backdrop-blur-xl shadow-2xl shadow-construction-darkblue/10' 
            : 'bg-white shadow-lg'
        }`}
      >
        {/* Decorative top line */}
        <div className="h-1 bg-gradient-to-r from-construction-yellow via-orange-400 to-construction-yellow"></div>
        
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center py-4">
            {/* Logo - Enhanced */}
            <Link to="/" className="flex items-center group">
              <div className="relative">
                {/* Glow effect behind logo */}
                <div className="absolute inset-0 bg-construction-yellow/20 rounded-xl blur-xl group-hover:blur-2xl transition-all duration-500 group-hover:scale-110"></div>
                
                <img 
                  src="/logo.png" 
                  alt="Construction Cards Helpline" 
                  className="relative h-14 md:h-16 w-auto object-contain transform group-hover:scale-105 transition-transform duration-300"
                />
              </div>
            </Link>

            {/* Desktop Navigation - Enhanced */}
            <nav className="hidden lg:flex items-center gap-1">
              {navigation.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`relative px-4 py-2 font-semibold transition-all duration-300 rounded-lg group ${
                    item.highlight
                      ? 'bg-construction-yellow text-construction-darkblue hover:bg-yellow-500'
                      : location.pathname === item.path
                        ? 'text-construction-yellow'
                        : 'text-gray-700 hover:text-construction-yellow'
                  }`}
                >
                  <span className="relative z-10">{item.name}</span>
                  
                  {/* Hover background - only for non-highlight items */}
                  {!item.highlight && (
                    <div className={`absolute inset-0 rounded-lg transition-all duration-300 ${
                      location.pathname === item.path
                        ? 'bg-construction-yellow/10 scale-100'
                        : 'bg-construction-yellow/0 group-hover:bg-construction-yellow/10 scale-95 group-hover:scale-100'
                    }`}></div>
                  )}
                  
                  {/* Active indicator */}
                  {location.pathname === item.path && !item.highlight && (
                    <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1/2 h-1 bg-gradient-to-r from-transparent via-construction-yellow to-transparent rounded-full"></div>
                  )}
                </Link>
              ))}
            </nav>

            {/* CTA Buttons Desktop - Enhanced */}
            <div className="hidden lg:flex items-center gap-3">
              <a
                href={`tel:${companyInfo.phone}`}
                className="relative group overflow-hidden bg-gradient-to-r from-construction-yellow to-yellow-500 text-construction-darkblue px-6 py-3 rounded-xl font-bold hover:shadow-2xl hover:shadow-construction-yellow/50 transition-all duration-300 hover:scale-105"
              >
                {/* Shine effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
                
                <span className="relative flex items-center gap-2">
                  <Phone size={20} />
                  Call Now
                </span>
              </a>

              <Link
                to="/contact"
                className="relative overflow-hidden bg-construction-darkblue text-white px-6 py-3 rounded-xl font-bold hover:bg-black transition-all duration-300 hover:scale-105 hover:shadow-2xl group"
              >
                <span className="relative">Apply Online</span>
                
                {/* Arrow animation */}
                <div className="absolute right-4 top-1/2 transform -translate-y-1/2 translate-x-8 group-hover:translate-x-0 transition-transform duration-300 opacity-0 group-hover:opacity-100">
                  →
                </div>
              </Link>
            </div>

            {/* Mobile Menu Button - Enhanced */}
            <div className="flex items-center gap-3 lg:hidden">
              <a
                href={`tel:${companyInfo.phone}`}
                className="flex items-center gap-2 bg-gradient-to-r from-construction-yellow to-yellow-500 text-construction-darkblue px-4 py-2.5 rounded-xl font-bold hover:shadow-lg transition-all duration-300 hover:scale-105"
              >
                <Phone size={18} />
                <span className="hidden sm:inline">Call</span>
              </a>
              
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="relative w-12 h-12 bg-construction-darkblue text-white rounded-xl hover:bg-black transition-all duration-300 flex items-center justify-center hover:scale-105 shadow-lg"
                aria-label="Toggle menu"
              >
                {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
                
                {/* Pulse effect when closed */}
                {!isMenuOpen && (
                  <div className="absolute inset-0 bg-construction-yellow/20 rounded-xl animate-ping"></div>
                )}
              </button>
            </div>
          </div>

          {/* Mobile Menu - Enhanced */}
          {isMenuOpen && (
            <nav className="lg:hidden pb-6 animate-in slide-in-from-top duration-300">
              <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-4 shadow-inner space-y-2">
                {navigation.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsMenuOpen(false)}
                    className={`block py-3 px-4 font-semibold rounded-xl transition-all duration-300 ${
                      location.pathname === item.path
                        ? 'bg-gradient-to-r from-construction-yellow to-yellow-400 text-construction-darkblue shadow-lg scale-105'
                        : 'text-gray-700 hover:bg-construction-yellow/10 hover:text-construction-yellow hover:translate-x-2'
                    }`}
                  >
                    {item.name}
                  </Link>
                ))}
                
                {/* Mobile CTA */}
                <div className="pt-4 space-y-3 border-t-2 border-gray-200">
                  <Link
                    to="/contact"
                    onClick={() => setIsMenuOpen(false)}
                    className="block text-center bg-construction-darkblue text-white py-3 px-4 rounded-xl font-bold hover:bg-black transition-all duration-300 shadow-lg"
                  >
                    Apply Online
                  </Link>
                </div>
              </div>
            </nav>
          )}
        </div>
      </header>
    </>
  );
};

export default Header;
